// src/components/Shared/PaymentMethodsModal.jsx
import React, { useEffect, useState } from 'react';
// import PaymentMethods from '../Account/PaymentMethods'; // Original component not used, logic is self-contained
import { motion, AnimatePresence } from 'framer-motion';
import { SUPPORT_LINKS } from '../../config/appConfig';

const PaymentMethodsModal = ({
    plan = {},
    actionType = 'upgrade', // 'upgrade' or 'extend'
    methods = [],
    paymentDetails = null, // Initially null, populated after method submission and backend response
    onClose,
    onConfirmWithMethod, // Callback: (plan, actionType, selectedMethod) => void
    initialPhase, // New prop: 'payment_successful', 'payment_error', 'initiating_payment', 'awaiting_payment_completion'
    statusMessage // New prop: message for success/error phase
}) => {

    const [selectedMethod, setSelectedMethod] = useState(null);
    // Modal phases: 'selecting_method', 'initiating_payment', 'awaiting_payment_completion', 'payment_error', 'payment_successful'
    const [modalPhase, setModalPhase] = useState('selecting_method'); // Default to selecting_method
    const [internalError, setInternalError] = useState(null);
    const [internalSuccessMessage, setInternalSuccessMessage] = useState(null);

    console.log("Methods: ", methods);
    console.log("PaymentMethodsModal received initialPhase:", initialPhase, "statusMessage:", statusMessage, "currentModalPhase:", modalPhase);

    useEffect(() => {
        // This effect handles phase transitions based on `initialPhase` and `paymentDetails` props.
        // It prioritizes `initialPhase` if it dictates a terminal state with a message.
        // Otherwise, it processes `paymentDetails` to open URLs or handle initiation errors.

        if (initialPhase) {
            setModalPhase(initialPhase); // Always set modalPhase if initialPhase is given
            if (initialPhase === 'payment_successful' && statusMessage) {
                setInternalSuccessMessage(statusMessage);
                setInternalError(null);
                return; // Fully handled by initialPhase for success with message
            } else if (initialPhase === 'payment_error' && statusMessage) {
                setInternalError(statusMessage);
                setInternalSuccessMessage(null);
                return; // Fully handled by initialPhase for error with message
            }
            // For other initialPhases (like 'awaiting_payment_completion', 'initiating_payment'),
            // we've set the modalPhase, but still might need to process paymentDetails below.
        }

        // Process paymentDetails if initialPhase didn't lead to an early return.
        if (paymentDetails) {
            if (paymentDetails.paymentUrl) {
                console.log("Payment Modal: URL received. Attempting to open:", paymentDetails.paymentUrl);
                setInternalError(null);
                try {
                    window.open(paymentDetails.paymentUrl, '_blank', 'noopener,noreferrer');
                    // If initialPhase was 'awaiting_payment_completion', modalPhase is already set.
                    // If initialPhase was undefined when this effect ran (e.g. on first mount with paymentDetails), set it now.
                    if (!initialPhase || initialPhase !== 'awaiting_payment_completion') {
                        // Ensure we are in the correct phase if not already dictated by a current initialPhase prop.
                        if (modalPhase !== 'awaiting_payment_completion') setModalPhase('awaiting_payment_completion');
                    }
                    console.log("Payment Modal: Transitioned to awaiting_payment_completion.");
                } catch (e) {
                    console.error("Payment Modal: Error during window.open:", e);
                    setInternalError("Could not open payment page. Please check pop-up blocker and try again.");
                    if (modalPhase !== 'payment_error') setModalPhase('payment_error');
                }
            } else { // paymentDetails exist but no URL, or has an error message.
                console.warn("Payment Modal: paymentDetails received without URL or with an error message:", paymentDetails.message || "No payment URL found.");
                setInternalError(paymentDetails.message || "Failed to obtain payment details. Please try again.");
                if (modalPhase !== 'payment_error') setModalPhase('payment_error');
            }
        } else if (!initialPhase && modalPhase !== 'initiating_payment' && modalPhase !== 'payment_error' && modalPhase !== 'payment_successful' && modalPhase !== 'awaiting_payment_completion') {
            // If NO paymentDetails, AND NO initialPhase, AND NOT in any processing/terminal state, revert to selection.
            // This is a general reset condition if the modal is effectively "cleared" by props.
            setModalPhase('selecting_method');
            setInternalError(null);
            setSelectedMethod(null);
        }
    }, [paymentDetails, initialPhase, statusMessage]); // modalPhase removed from here as per original structure, specific phase checks handle it.

    // This effect primarily handles resets or adherence to initialPhase when plan/actionType changes,
    // or when initialPhase itself changes. It also makes terminal states "sticky".
    useEffect(() => {
        if (initialPhase) {
            // If initialPhase is explicitly provided, always honor it.
            // This is the primary way AccountPage controls the modal's state.
            setModalPhase(initialPhase);
            if (initialPhase === 'payment_successful' && statusMessage) {
                setInternalSuccessMessage(statusMessage);
                setInternalError(null); // Clear any previous error
            } else if (initialPhase === 'payment_error' && statusMessage) {
                setInternalError(statusMessage);
                setInternalSuccessMessage(null); // Clear any previous success message
            }
        } else {
            // initialPhase is NOT provided (e.g., undefined).
            // Reset to 'selecting_method' ONLY IF we are not already in a specific
            // user-facing terminal state (like success or error) or a payment processing state.
            // This prevents resetting from 'payment_successful' if initialPhase is just temporarily
            // missing during a complex re-render sequence but the modal wasn't meant to reset yet.
            if (
                modalPhase !== 'payment_successful' &&
                modalPhase !== 'payment_error' &&
                modalPhase !== 'awaiting_payment_completion' && // Don't reset if waiting for payment
                modalPhase !== 'initiating_payment' // Don't reset if initiating
            ) {
                setModalPhase('selecting_method');
                setSelectedMethod(null); // Clear selection on reset
                setInternalError(null); // Clear errors on reset
                setInternalSuccessMessage(null); // Clear success messages on reset
            }
            // If modalPhase IS 'payment_successful', 'payment_error', 'awaiting_payment_completion', or 'initiating_payment',
            // and initialPhase is now undefined, we "stick" to the current phase.
            // The parent (AccountPage) should explicitly close or provide a new, valid initialPhase
            // to change the modal from these states.
        }
    }, [plan, actionType, initialPhase, statusMessage, modalPhase]); // modalPhase IS needed here due to reading it in the conditional logic.


    const handleMethodSelect = (method) => {
        if (modalPhase !== 'selecting_method') return;
        console.log("Selected Method:", method.name);
        setSelectedMethod(method);
        setInternalError(null);
    };

    const handleConfirmAndInitiatePayment = () => {
        if (modalPhase !== 'selecting_method' || !selectedMethod) return;

        console.log("Confirm Payment clicked for plan:", plan.name, "with method:", selectedMethod?.name);
        setInternalError(null);
        setModalPhase('initiating_payment'); // Show spinner in modal, awaiting AccountPage callback and new paymentDetails
        onConfirmWithMethod(plan, actionType, selectedMethod);
    };

    const getModalTitle = () => {
        switch (modalPhase) {
            case 'selecting_method': return 'Select Payment Method';
            case 'initiating_payment': return 'Initiating Payment...';
            case 'awaiting_payment_completion': return 'Awaiting Payment Confirmation';
            case 'payment_error': return 'Payment Error';
            case 'payment_successful': return 'Payment Successful'; // New Title
            default: return 'Payment';
        }
    };

    return (
        <div className="relative w-full max-w-xl">
            <div className="bg-dark-700 rounded-xl p-4 mb-6 border border-dark-400 relative overflow-hidden">
                <div className="flex items-center">
                    <div className={`w-12 h-12 rounded-lg flex items-center justify-center mr-4 ${plan.isCurrent && actionType === 'extend' ? 'bg-gradient-to-br from-accent-purple to-accent-blue' : 'bg-accent-pink/20'
                        }`}>
                        <i className={`fas ${plan.isCurrent && actionType === 'extend' ? 'fa-gem' : (plan.icon || 'fa-crown')
                            } text-white text-lg`}></i>
                    </div>
                    <div>
                        <h3 className="font-bold text-lg">
                            {actionType === 'extend' ? `Renew ${plan.name} Plan` : `Upgrade to ${plan.name} Plan`}
                        </h3>
                        <div className="flex items-center mt-1">
                            <span className="text-accent-purple font-medium mr-3">{plan.price}{plan.period}</span>
                            {plan.isCurrent && actionType === 'extend' && (
                                <span className="text-xs bg-dark-600 text-white px-2 py-0.5 rounded">
                                    Current Plan
                                </span>
                            )}
                        </div>
                    </div>
                </div>
                <div className="absolute -right-10 -top-10 w-32 h-32 bg-accent-purple/10 rounded-full blur-xl"></div>
            </div>

            <div className="flex justify-between items-center mb-4">
                <h2 className="text-lg font-medium text-gray-300">{getModalTitle()}</h2>
                <button
                    onClick={onClose}
                    className="text-gray-400 hover:text-white transition transform-gpu hover:scale-110"
                    disabled={modalPhase === 'awaiting_payment_completion' || modalPhase === 'initiating_payment'} // Prevent close during critical async ops handled by system
                >
                    <i className="fas fa-times"></i>
                </button>
            </div>

            <AnimatePresence mode="wait">
                {modalPhase === 'payment_successful' && (
                    <motion.div
                        key="success_phase"
                        initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0, scale: 0.9 }} transition={{ duration: 0.2 }}
                        className="text-center py-10 px-6 bg-dark-600 rounded-2xl border border-accent-green/50"
                    >
                        <i className="fas fa-check-circle text-accent-green text-5xl mb-4"></i>
                        <p className="text-xl font-semibold mb-2 text-gray-100">Payment Confirmed!</p>
                        <p className="text-gray-300 text-sm mb-6">{internalSuccessMessage || `Your ${plan.name} plan is now active.`}</p>

                        {/* Community Links Section */}
                        <div className="bg-dark-700/50 rounded-xl p-4 mb-6 border border-dark-500">
                            <p className="text-sm font-medium text-gray-200 mb-3">
                                <i className="fas fa-users mr-2 text-accent-purple"></i>
                                Join our community for updates and support!
                            </p>
                            <div className="flex justify-center space-x-3">
                                <motion.a
                                    whileHover={{ scale: 1.05 }}
                                    whileTap={{ scale: 0.95 }}
                                    href={SUPPORT_LINKS.discord}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="flex items-center space-x-2 bg-[#5865F2]/20 hover:bg-[#5865F2]/30 text-[#5865F2] rounded-lg px-4 py-2 transition-all duration-200"
                                >
                                    <i className="fab fa-discord text-lg"></i>
                                    <span className="font-medium">Discord</span>
                                </motion.a>
                                <motion.a
                                    whileHover={{ scale: 1.05 }}
                                    whileTap={{ scale: 0.95 }}
                                    href={SUPPORT_LINKS.telegram}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="flex items-center space-x-2 bg-[#0088cc]/20 hover:bg-[#0088cc]/30 text-[#0088cc] rounded-lg px-4 py-2 transition-all duration-200"
                                >
                                    <i className="fab fa-telegram text-lg"></i>
                                    <span className="font-medium">Telegram</span>
                                </motion.a>
                            </div>
                            <p className="text-xs text-gray-400 mt-3">
                                Stay updated with the latest features and get help from our team
                            </p>
                        </div>

                        <motion.button
                            whileHover={{ scale: 1.02, y: -2 }} whileTap={{ scale: 0.98 }}
                            onClick={onClose}
                            className="w-full py-3 bg-gradient-to-r from-accent-green to-accent-blue text-white rounded-xl hover:shadow-glow transition font-medium"
                        >
                            Done
                        </motion.button>
                    </motion.div>
                )}
                {/* Keep other phases below, ensuring only one is rendered due to AnimatePresence mode="wait" and conditional rendering */}
{modalPhase === 'selecting_method' && (
    <motion.div
        key="select_method_phase"
        initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -10 }} transition={{ duration: 0.2 }}
    >
        {methods && methods.length > 0 ? (
            <div className="bg-dark-600 rounded-2xl p-6 gradient-border transform-gpu hover:-translate-y-0.5 hover:shadow-lg transition-medium mb-6">
                <div className="grid grid-cols-3 gap-4">
                    {methods.map((method, index) => (
                        <motion.div
                            key={method.id || index}
                            whileHover={{ y: -2, scale: 1.02 }} whileTap={{ scale: 0.98 }}
                            className={`bg-dark-500 rounded-xl p-4 border flex items-center cursor-pointer group hover:border-accent-blue/40 transition-all duration-150
                                ${selectedMethod?.id === method.id ? 'border-accent-blue ring-1 ring-accent-blue/50' : 'border-dark-400'}
                            `}
                            onClick={() => handleMethodSelect(method)}
                        >
                            <div className={`w-10 h-10 rounded-lg bg-dark-400 flex items-center justify-center mr-3 ${method.iconColor || ''} group-hover:bg-gradient-to-br group-hover:from-accent-purple group-hover:to-accent-blue transition-medium`}>
                                <i className={`fas ${method.icon} group-hover:text-white transition-medium ${selectedMethod?.id === method.id ? 'text-white' : ''}`}></i>
                            </div>
                            <div>
                                <p className={`font-medium group-hover:text-accent-purple transition-medium ${selectedMethod?.id === method.id ? 'text-accent-purple' : ''}`}>{method.name}</p>
                                <p className="text-xs text-gray-400 group-hover:text-gray-300 transition-medium">{method.description}</p>
                            </div>
                            <div className="ml-auto opacity-0 group-hover:opacity-100 transition-medium">
                                {selectedMethod?.id === method.id ? (
                                    <i className="fas fa-check-circle text-xs text-accent-green"></i>
                                ) : (
                                    <i className="fas fa-chevron-right text-xs text-gray-400"></i>
                                )}
                            </div>
                        </motion.div>
                    ))}
                </div>
                <p className="text-center text-xs text-gray-500 mt-4 group hover:text-gray-400 transition-medium">
                    <i className="fas fa-lock mr-1 group-hover:text-accent-green transition-medium"></i> Secure Payment Gateway
                </p>
            </div>
        ) : (
            <div className="text-center text-gray-400 py-6 mb-6">No payment methods available.</div>
        )}
        
        {/* Terms and Conditions Notice */}
        <div className="text-center text-xs text-gray-400 mb-4">
            By proceeding with payment, you agree to our{' '}
            <a 
                href="/terms" 
                target="_blank" 
                rel="noopener noreferrer" 
                className="text-accent-blue hover:text-accent-purple transition-colors underline"
            >
                Terms of Service
            </a>
        </div>
        
        <motion.button
            whileHover={{ scale: 1.02, y: -2 }} whileTap={{ scale: 0.98 }}
            onClick={handleConfirmAndInitiatePayment}
            disabled={!selectedMethod}
            className="w-full py-3 bg-gradient-to-r from-accent-green to-accent-blue text-white rounded-xl hover:shadow-glow transition font-medium disabled:opacity-60 disabled:cursor-not-allowed disabled:hover:shadow-none flex items-center justify-center"
        >
            <i className="fas fa-check-circle mr-2"></i>
            Confirm with {selectedMethod ? selectedMethod.name : 'Selected Method'}
        </motion.button>
    </motion.div>
)}

                {modalPhase === 'initiating_payment' && (
                    <motion.div
                        key="initiating_phase"
                        initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0, scale: 0.9 }} transition={{ duration: 0.2 }}
                        className="text-center py-10 px-6 bg-dark-600 rounded-2xl"
                    >
                        <i className="fas fa-spinner fa-spin text-accent-purple text-4xl mb-4"></i>
                        <p className="text-lg font-medium mb-2">Preparing Your Payment...</p>
                        <p className="text-gray-400 text-sm">Please wait while we connect to the payment provider.</p>
                    </motion.div>
                )}

                {modalPhase === 'awaiting_payment_completion' && (
                    <motion.div
                        key="awaiting_phase"
                        initial={{ opacity: 0, scale: 0.9 }}
                        animate={{ opacity: 1, scale: 1 }}
                        exit={{ opacity: 0, scale: 0.9 }}
                        transition={{ duration: 0.2 }}
                        className="text-center py-10 px-6 bg-dark-600 rounded-2xl"
                    >
                        <i className="fas fa-hourglass-half fa-spin text-accent-blue text-4xl mb-4"></i>
                        <p className="text-lg font-medium mb-2">Waiting for Payment Confirmation</p>
                        <p className="text-gray-400 text-sm mb-2">
                            Your payment page should have opened in a new tab.
                            This window will update automatically once confirmed.
                        </p>

                        {paymentDetails?.paymentUrl && (
                            <a
                                href={paymentDetails.paymentUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-accent-blue hover:underline text-xs mt-4 inline-block"
                            >
                                <i className="fas fa-external-link-alt mr-1"></i>Re-open payment page
                            </a>
                        )}

                        {/* Support Links */}
                        <div className="mt-6 pt-6 border-t border-dark-500">
                            <p className="text-xs text-gray-400 mb-3">Need help? Contact us:</p>
                            <div className="flex justify-center space-x-3">
                                <motion.a
                                    whileHover={{ scale: 1.05 }}
                                    whileTap={{ scale: 0.95 }}
                                    href={SUPPORT_LINKS.discord} // Замените на вашу ссылку
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="flex items-center space-x-1.5 bg-[#5865F2]/20 hover:bg-[#5865F2]/30 text-[#5865F2] rounded-lg px-3 py-1.5 transition-all duration-200 text-xs"
                                >
                                    <i className="fab fa-discord"></i>
                                    <span>Discord</span>
                                </motion.a>
                                <motion.a
                                    whileHover={{ scale: 1.05 }}
                                    whileTap={{ scale: 0.95 }}
                                    href={SUPPORT_LINKS.telegram} // Замените на вашу ссылку
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="flex items-center space-x-1.5 bg-[#0088cc]/20 hover:bg-[#0088cc]/30 text-[#0088cc] rounded-lg px-3 py-1.5 transition-all duration-200 text-xs"
                                >
                                    <i className="fab fa-telegram"></i>
                                    <span>Telegram</span>
                                </motion.a>
                            </div>
                        </div>
                    </motion.div>
                )}

                {modalPhase === 'payment_error' && (
                    <motion.div
                        key="error_phase"
                        initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -10 }}
                        className="text-center py-10 px-6 bg-dark-600 rounded-2xl border border-accent-red/50"
                    >
                        <i className="fas fa-exclamation-triangle text-accent-red text-4xl mb-4"></i>
                        <p className="text-lg font-medium mb-2 text-accent-red">Error</p>
                        <p className="text-gray-300 text-sm mb-4">{internalError || "An unexpected error occurred."}</p>
                        <button
                            onClick={onClose} // Allow closing on error
                            className="px-4 py-2 bg-accent-red/80 hover:bg-accent-red text-white rounded-lg text-sm font-medium"
                        >
                            Close
                        </button>
                    </motion.div>
                )}
            </AnimatePresence>

            {internalError && modalPhase === 'selecting_method' && ( // Show minor errors during selection phase only
                <p className="text-xs text-accent-red mt-3 text-center">{internalError}</p>
            )}
        </div>
    );
};

export default PaymentMethodsModal;